import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { KBSidebar } from "@/components/KBSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, FoldersIcon as Folders, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, FolderPlusIcon as FolderPlus, BookOpenIcon as BookOpen } from "@phosphor-icons/react";

export default function KnowledgeBase() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [catDlg, setCatDlg] = useState(null);   // {id?, nome, descrizione, icona, colore, ordine}
  const [folderDlg, setFolderDlg] = useState(null); // {categoria_id, nome, descrizione}

  const load = useCallback(() => {
    api.get("/kb/categories").then(({ data }) => setCats(data || [])).catch(() => setCats([]));
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const doSearch = async (term) => {
    setQ(term);
    if (!term.trim()) { setResults(null); return; }
    try { const { data } = await api.get(`/kb/search`, { params: { q: term } }); setResults(data || []); }
    catch { setResults([]); }
  };

  const saveCat = async () => {
    const c = catDlg;
    if (!c.nome?.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      if (c.id) await api.put(`/kb/categories/${c.id}`, c);
      else await api.post(`/kb/categories`, c);
      toast.success("Categoria salvata"); setCatDlg(null); setReloadKey((k) => k + 1);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const delCat = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.nome}" e tutte le sue cartelle/articoli?`)) return;
    try { await api.delete(`/kb/categories/${c.id}`); toast.success("Categoria eliminata"); setReloadKey((k) => k + 1); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const saveFolder = async () => {
    const f = folderDlg;
    if (!f.nome?.trim()) { toast.error("Nome obbligatorio"); return; }
    try { await api.post(`/kb/folders`, f); toast.success("Cartella creata"); setFolderDlg(null); setReloadKey((k) => k + 1); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <InternalLayout>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2"><BookOpen size={24} weight="fill" className="text-blue-600" /> Knowledge Base</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Procedure, guide e soluzioni tecniche interne.</p>
          </div>
          <Button onClick={() => setCatDlg({ nome: "", descrizione: "", icona: "folder-notch", colore: "", ordine: cats.length })} className="rounded-sm gap-1.5" data-testid="kb-new-category-btn"><Plus size={16} /> Nuova categoria</Button>
        </div>

        <div className="relative mb-5 max-w-2xl">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Cerca articoli, procedure, comandi…" className="pl-10 h-11 rounded-sm" data-testid="kb-search-input" />
        </div>

        <div className="flex gap-5">
          <KBSidebar reloadKey={reloadKey} />
          <div className="flex-1 min-w-0">
            {results !== null ? (
              <div data-testid="kb-search-results">
                <p className="text-sm font-medium text-slate-700 mb-3">{results.length} risultati per “{q}”</p>
                <div className="space-y-2">
                  {results.map((r) => (
                    <Card key={r.id} className="rounded-sm shadow-none p-3 hover:border-blue-300 cursor-pointer" onClick={() => nav(`/knowledge-base/article/${r.id}`)} data-testid={`kb-result-${r.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{r.titolo}</span>
                        <StatoBadge stato={r.stato} />
                        {r.visibile_area_cliente && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Area cliente</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.categoria_nome} › {r.cartella_nome}</p>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.snippet}</p>
                    </Card>
                  ))}
                  {results.length === 0 && <p className="text-sm text-muted-foreground">Nessun risultato.</p>}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Categorie ({cats.length})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {cats.map((c) => (
                    <Card key={c.id} className="rounded-sm shadow-none p-4 group" data-testid={`kb-cat-card-${c.id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Folders size={18} weight="fill" className="text-slate-500" />
                        <span className="font-semibold text-slate-800 flex-1 truncate">{c.nome}</span>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                          <button title="Aggiungi cartella" onClick={() => setFolderDlg({ categoria_id: c.id, nome: "", descrizione: "" })} className="p-1 text-muted-foreground hover:text-blue-600" data-testid={`kb-add-folder-${c.id}`}><FolderPlus size={15} /></button>
                          <button title="Modifica" onClick={() => setCatDlg({ ...c })} className="p-1 text-muted-foreground hover:text-slate-700" data-testid={`kb-edit-cat-${c.id}`}><PencilSimple size={15} /></button>
                          <button title="Elimina" onClick={() => delCat(c)} className="p-1 text-muted-foreground hover:text-rose-600" data-testid={`kb-del-cat-${c.id}`}><Trash size={15} /></button>
                        </div>
                      </div>
                      <div className="divide-y divide-border/60">
                        {(c.folders || []).slice(0, 3).map((f) => (
                          <button key={f.id} onClick={() => nav(`/knowledge-base/folder/${f.id}`)} className="w-full flex items-center justify-between py-2 text-left hover:text-blue-600" data-testid={`kb-card-folder-${f.id}`}>
                            <span className="text-sm text-slate-600 truncate">{f.nome}</span>
                            <span className="text-xs font-semibold text-muted-foreground">{String(f.articoli_count).padStart(2, "0")}</span>
                          </button>
                        ))}
                        {c.folders_count === 0 && <p className="py-2 text-xs text-muted-foreground italic">Nessuna cartella — usa + per aggiungerne</p>}
                      </div>
                      {c.folders_count > 3 && (
                        <button onClick={() => nav(`/knowledge-base/folder/${c.folders[0].id}`)} className="mt-2 text-sm text-blue-600 hover:underline" data-testid={`kb-see-all-${c.id}`}>
                          Vedi tutte le {String(c.folders_count).padStart(2, "0")} cartelle
                        </button>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog categoria */}
      <Dialog open={!!catDlg} onOpenChange={(o) => !o && setCatDlg(null)}>
        <DialogContent className="rounded-sm" data-testid="kb-cat-dialog">
          <DialogHeader><DialogTitle>{catDlg?.id ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          {catDlg && (
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input value={catDlg.nome} onChange={(e) => setCatDlg({ ...catDlg, nome: e.target.value })} className="rounded-sm" data-testid="kb-cat-name" /></div>
              <div><Label>Descrizione</Label><Textarea value={catDlg.descrizione} onChange={(e) => setCatDlg({ ...catDlg, descrizione: e.target.value })} rows={2} className="rounded-sm" /></div>
              <div className="flex gap-3">
                <div className="flex-1"><Label>Ordine</Label><Input type="number" value={catDlg.ordine} onChange={(e) => setCatDlg({ ...catDlg, ordine: e.target.value })} className="rounded-sm" /></div>
                <div className="flex-1"><Label>Colore (opz.)</Label><Input value={catDlg.colore} onChange={(e) => setCatDlg({ ...catDlg, colore: e.target.value })} placeholder="#2563eb" className="rounded-sm" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCatDlg(null)} className="rounded-sm">Annulla</Button><Button onClick={saveCat} className="rounded-sm" data-testid="kb-cat-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog cartella */}
      <Dialog open={!!folderDlg} onOpenChange={(o) => !o && setFolderDlg(null)}>
        <DialogContent className="rounded-sm" data-testid="kb-folder-dialog">
          <DialogHeader><DialogTitle>Nuova cartella</DialogTitle></DialogHeader>
          {folderDlg && (
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input value={folderDlg.nome} onChange={(e) => setFolderDlg({ ...folderDlg, nome: e.target.value })} className="rounded-sm" data-testid="kb-folder-name" /></div>
              <div><Label>Descrizione</Label><Textarea value={folderDlg.descrizione} onChange={(e) => setFolderDlg({ ...folderDlg, descrizione: e.target.value })} rows={2} className="rounded-sm" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setFolderDlg(null)} className="rounded-sm">Annulla</Button><Button onClick={saveFolder} className="rounded-sm" data-testid="kb-folder-save">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}

export function StatoBadge({ stato }) {
  const map = {
    bozza: ["Bozza", "bg-amber-100 text-amber-700"],
    pubblicato: ["Pubblicato", "bg-emerald-100 text-emerald-700"],
    archiviato: ["Archiviato", "bg-slate-200 text-slate-600"],
  };
  const [label, cls] = map[stato] || ["—", "bg-slate-100 text-slate-600"];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${cls}`} data-testid={`kb-stato-${stato}`}>{label}</span>;
}
